import * as React from 'react'

import { useHistory } from 'react-router'

import { useAppRoutes } from '@webapp/hooks'
import { useSetOrderRequestFlowPreferencePresenter } from '@webapp/platform/iam'
import { useConversation } from './ConversationProvider'
import { GUIDED_INTAKE_EVENTS } from '../constants'
import { useCart, type UseCartResult } from '../hooks/useCart'
import { useRecentConversations } from '../hooks/useRecentConversations'
import { type ConversationComponent } from '../value-objects/ConversationTurnItem'
import {
  deriveCartSourceChannel,
  deriveSourceChannel,
} from '../value-objects/ConversationTurnItem/deriveSourceChannel'
import {
  ConversationComponentTypes,
  type ConversationComponentTypeEnum,
  ConversationItemKinds,
} from '../value-objects/enums'
import { type OrderRequestRedirectState } from '../value-objects/OrderRequestRedirectState'

export type CartContextValue = UseCartResult

// The reserved tab is only usable if the user left it alone: a closed window
// stays a truthy reference, and navigating it is at best a silent no-op.
const sendToCheckout = (
  reservedTab: Nullable<Window>,
  checkoutUrl: string
): void => {
  try {
    if (reservedTab && !reservedTab.closed) {
      reservedTab.location.replace(checkoutUrl)
      return
    }
  } catch {
    // fall through to a fresh tab
  }
  window.open(checkoutUrl, '_blank', 'noopener,noreferrer')
}

export const CartContext = React.createContext<Nullable<CartContextValue>>(null)

const selectLatestComponent = <TType extends ConversationComponentTypeEnum>(
  items: ReturnType<typeof useConversation>['items'],
  types: readonly TType[]
): Nullable<Extract<ConversationComponent, { type: TType }>> => {
  const isSelectedType = (
    component: ConversationComponent
  ): component is Extract<ConversationComponent, { type: TType }> =>
    types.some((type) => type === component.type)

  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index]
    if (item.kind !== ConversationItemKinds.ENVELOPE) continue
    for (
      let componentIndex = item.components.length - 1;
      componentIndex >= 0;
      componentIndex -= 1
    ) {
      const component = item.components[componentIndex]
      if (isSelectedType(component)) return component
    }
  }
  return null
}

export const CartProvider = ({
  conversationLabel,
  onContinue,
  children,
}: React.PropsWithChildren<{
  conversationLabel: Nullable<string>
  onContinue: () => void
}>) => {
  const { items, status, sendSilentMessage, conversationId } = useConversation()
  const { url } = useAppRoutes()
  const history = useHistory()
  const {
    commands: { setPreferrenceToAIFlow },
  } = useSetOrderRequestFlowPreferencePresenter()
  const { addRecentConversation, removeRecentConversation } =
    useRecentConversations()

  const cart = React.useMemo(
    () =>
      selectLatestComponent(items, [
        ConversationComponentTypes.CART,
        ConversationComponentTypes.CHECKOUT,
      ]),
    [items]
  )

  const vendorRedirectUrl = React.useMemo(
    () =>
      selectLatestComponent(items, [
        ConversationComponentTypes.ORDER_CONFIRMATION,
      ])?.vendorRedirectUrl ?? null,
    [items]
  )

  // A conversation only becomes worth resuming once the agent has produced a
  // cart. Gated on hasCart (not cart identity) so streamed cart updates within
  // a conversation don't rewrite the entry. Resumed conversations carry no
  // label; their recent entry was already refreshed when the user reopened
  // them.
  const hasCart = cart !== null
  React.useEffect(() => {
    if (!hasCart || conversationId === null || conversationLabel === null)
      return
    addRecentConversation({ conversationId, label: conversationLabel })
  }, [hasCart, conversationId, conversationLabel, addRecentConversation])

  const handleContinued = React.useCallback(
    (reservedTab: Nullable<Window>) => {
      // The preference is only forced once the user commits to the request the
      // conversation built — exploring Guided Intake without continuing must not
      // change their classic/AI choice.
      setPreferrenceToAIFlow()

      window.analytics?.track(GUIDED_INTAKE_EVENTS.DRAFT_OR_CREATED, {
        conversation_id: conversationId,
        cart_id: cart?.cartId ?? null,
        source_channel: cart?.vendors
          ? deriveCartSourceChannel(cart.vendors)
          : deriveSourceChannel(
              cart?.type === ConversationComponentTypes.CART
                ? cart.vendor
                : null
            ),
        item_count: cart?.totalItems ?? null,
      })

      if (conversationId !== null) {
        removeRecentConversation(conversationId)
      }

      // A vendor that owns its own checkout finishes it on the vendor's site, in
      // the tab reserved on the click, so the order request the conversation
      // built stays open in Procurify. A checkout that needs no redirect has no
      // use for the reserved tab.
      if (vendorRedirectUrl) {
        window.analytics?.track(GUIDED_INTAKE_EVENTS.CHECKOUT_REDIRECT, {
          conversation_id: conversationId,
          cart_id: cart?.cartId ?? null,
        })
        sendToCheckout(reservedTab, vendorRedirectUrl)
      } else {
        reservedTab?.close()
      }

      // The continue turn replaced the draft server-side, so the redirect is
      // flagged for the order request page to drop its cached copy — an unflagged
      // navigation to the same route must never touch that cache.
      const state: OrderRequestRedirectState = { orderDraftReplaced: true }
      history.push(url('OrderRequestPage'), state)
      onContinue()
    },
    [
      setPreferrenceToAIFlow,
      conversationId,
      cart,
      removeRecentConversation,
      vendorRedirectUrl,
      history,
      url,
      onContinue,
    ]
  )

  const value = useCart({
    cart,
    status,
    sendSilentMessage,
    onContinued: handleContinued,
  })

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCartContext = (): CartContextValue => {
  const context = React.useContext(CartContext)
  if (!context) {
    throw new Error('useCartContext must be used within a CartProvider')
  }
  return context
}
